import { format } from 'date-fns';

const formatCurrency = (
  amount: number,
  currency: string = '',
  locale: string = 'en-GB',
) => {
  if (!currency) {
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount ?? 0);
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency ?? 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount ?? 0);
};

const formatCurrencyNoUnit = (amount: number, locale: string = 'en-GB') => {
  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount ?? 0);
};

const formatDate = (value: any, withTime = false) => {
  if (!value) return '';

  try {
    return format(value, `yyyy-MM-dd${withTime ? ' HH:mm:ss' : ''}`);
  } catch (e) {
    return value;
  }
};

const formatDisplayDate = (value: any) => {
  if (!value) return '';

  try {
    return format(value, 'dd/MM/yyyy');
  } catch (e) {
    return String(value);
  }
};

const formatDisplayDateTime = (value: any) => {
  if (!value) return '';

  try {
    return format(value, 'dd/MM/yyyy HH:mm');
  } catch (e) {
    return String(value);
  }
};

/**
 * Formats a context field value: supports { value, currency } money objects, plain numbers, or other values.
 */
function formatContextValue(value: unknown): string {
  if (
    value != null &&
    typeof value === 'object' &&
    'value' in (value as Record<string, unknown>)
  ) {
    const m = value as { value: number; currency?: string };
    return formatCurrency(m.value, m.currency);
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return formatCurrency(value);
  }
  return String(value ?? '');
}

/**
 * Numeric value for filtering (e.g. hide zeros). Handles both plain number and { value, currency }.
 */
function getContextNumericValue(value: unknown): number | null {
  if (
    value != null &&
    typeof value === 'object' &&
    'value' in (value as Record<string, unknown>)
  ) {
    const n = Number((value as { value: number }).value);
    return Number.isNaN(n) ? null : n;
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  return null;
}

export {
  formatCurrency,
  formatDate,
  formatDisplayDate,
  formatDisplayDateTime,
  formatCurrencyNoUnit,
  formatContextValue,
  getContextNumericValue,
};
